import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReadOnlyService } from '../services/read-only.service';

/**
 * Stops a Viewer's clicks on write controls, and dims them so they look disabled.
 *
 * This has frozen the app twice while under development, from two DIFFERENT causes — both worth
 * understanding, because the second one is easy to reintroduce by accident:
 *
 *  1. An earlier version scanned the whole page with `querySelectorAll` inside the mutation
 *     callback itself, once per mutation batch. A table render fires mutations in quick succession,
 *     so that was hundreds of full-page scans in a couple of seconds.
 *  2. Throttling the scan (as done below) fixed #1, but the app still froze on the same page,
 *     because **this is a zone.js app, and zone.js patches `MutationObserver`, `setTimeout` and
 *     `addEventListener` globally.** By default, Angular runs a full change-detection pass over the
 *     ENTIRE component tree every time any zone-patched callback finishes — regardless of how little
 *     work that callback does. A throttled callback that only sets a boolean flag still counts: if it
 *     fires 300 times while a table renders, that's 300 full app-wide CD cycles, and on a
 *     data-heavy page each one is expensive enough to add up to exactly the frozen-progress-bar
 *     symptom this was supposed to avoid.
 *
 * The fix for #2 is `NgZone.runOutsideAngular()`: everything here — the observers, the click
 * listener, their timers — is set up inside it, so none of those callbacks are zone-tracked and
 * NONE of them trigger change detection merely by firing. This is safe specifically because nothing
 * in this class touches Angular-bound state; it only reads/writes plain DOM attributes. The one
 * exception is the snackbar shown when a click is blocked — that's real UI needing to render, so
 * that single call is wrapped back in `ngZone.run()` to re-enter the zone just for it.
 *
 *  - **The click-guard is O(1) per click**, not proportional to the page. It does nothing until a
 *    user actually clicks something, and even then it's one `closest()` lookup, not a page scan.
 *    This is the *real* protection layer, and the only one that's exact and instant everywhere —
 *    including a button that mutates local component state before ever calling the API.
 *  - **Every observer here does zero work in its mutation callback.** It only sets a "dirty" flag.
 *    The actual scan is coalesced onto a trailing timer, so no matter how many mutations fire in a
 *    burst, at most one scan runs per debounce window (see OVERLAY_DEBOUNCE_MS / BODY_DEBOUNCE_MS).
 *    Each scan is a single native `querySelectorAll` call; the `MARKER` attribute makes repeat scans
 *    cheaper still, since already-handled controls short-circuit immediately.
 *  - `.cdk-overlay-container` (Material dialogs/menus/selects) gets a short debounce for snappy
 *    dimming, since it only ever holds currently-open overlays. The rest of the document gets a
 *    longer one — inline buttons that render asynchronously after a route resolves (a form loading
 *    its own data before showing "Save & Continue", for instance) are what this covers; a per-route
 *    scan alone missed exactly that case.
 *
 * If this needs changing later, the invariants to preserve are both: "the callback never scans, only
 * ever flags," AND "none of this runs inside the Angular zone." Losing either one reproduces one of
 * the two freezes above.
 */
@Injectable({ providedIn: 'root' })
export class ReadOnlyUiGuard {
  private readonly readOnly = inject(ReadOnlyService);
  private readonly router = inject(Router);
  private readonly snack = inject(MatSnackBar);
  private readonly ngZone = inject(NgZone);

  // Verbs that announce a write. Matched as whole words against a control's visible text, aria-label,
  // title and Material tooltip — so an icon-only <mat-icon>delete</mat-icon> button is caught too.
  private static readonly WRITE_WORDS = [
    'save', 'submit', 'create', 'add', 'add_circle', 'new', 'delete', 'delete_outline', 'delete_forever',
    'remove', 'update', 'approve', 'reject', 'void', 'confirm', 'apply', 'send', 'upload', 'assign',
    'unassign', 'archive', 'restore', 'duplicate', 'merge', 'generate', 'issue', 'post', 'pay',
    'write-off', 'write_off', 'complete', 'finalise', 'finalize', 'publish', 'invite', 'revoke',
  ];

  private static readonly WRITE_PATTERN = new RegExp(
    `(^|[^a-z])(${ReadOnlyUiGuard.WRITE_WORDS.join('|')})([^a-z]|$)`, 'i'
  );

  private static readonly CONTROL_SELECTOR =
    'button, input[type="submit"], input[type="button"], [mat-menu-item], a[mat-button], a[mat-flat-button]';

  // Marks what the dimming pass disabled, so undo touches nothing it didn't touch — a control that
  // was already disabled for its own reasons must stay that way.
  private static readonly MARKER = 'data-bc-readonly-disabled';

  /** Put on a control that opens or navigates only, so its label is never read as a write. */
  private static readonly READ_SAFE = 'data-bc-read-safe';

  // Overlay content (dialogs/menus) is small, so it can afford a snappy debounce. The rest of the
  // document is bigger and changes more, so it gets a longer one — still frequent enough that async-
  // loaded buttons get dimmed within half a second of appearing.
  private static readonly OVERLAY_DEBOUNCE_MS = 150;
  private static readonly BODY_DEBOUNCE_MS = 500;

  private active = false;
  private overlayObserver?: MutationObserver;
  private bootstrapObserver?: MutationObserver;
  private overlayTimer?: ReturnType<typeof setTimeout>;
  private bodyObserver?: MutationObserver;
  private bodyTimer?: ReturnType<typeof setTimeout>;
  private bodyDirty = false;

  /** Called once at app start. */
  start(): void {
    this.readOnly.readOnly$.subscribe(readOnly => {
      this.active = readOnly;
      document.body.classList.toggle('bc-read-only', readOnly);
      if (readOnly) {
        // Everything from here down is DOM plumbing (observers, timers) that must never be
        // zone-tracked — see the class comment for why. This is the one legitimate place this
        // service touches Angular state (`this.active`, the body class), and that's already done
        // above; nothing below needs the zone.
        this.ngZone.runOutsideAngular(() => {
          this.scanBody();
          this.watchOverlayContainer();
          this.watchBody();
        });
      } else {
        this.stopWatchingOverlay();
        this.stopWatchingBody();
        this.undo();
      }
    });

    // Cheap insurance on top of the body watcher below: covers the moment read-only becomes active
    // mid-route, before any mutation has fired yet.
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      if (this.active) this.ngZone.runOutsideAngular(() => this.scanBody());
    });

    this.ngZone.runOutsideAngular(() => this.installClickGuard());
  }

  // ─── Click guard: the actual functional protection ──────────────────────────────────────────

  private installClickGuard(): void {
    // Capture phase on document runs before Angular's own (click) binding on the target element, so
    // stopImmediatePropagation here means the bound handler — and whatever it would have saved —
    // never runs at all. Registered outside the zone (see start()), so a click that ISN'T on a write
    // control — the overwhelming majority — costs one closest() lookup with no change-detection
    // side effect at all.
    document.addEventListener('click', event => {
      if (!this.active) return;
      const target = event.target as HTMLElement | null;
      const control = target?.closest<HTMLElement>(ReadOnlyUiGuard.CONTROL_SELECTOR);
      if (!control || this.isAlreadyDisabled(control) || !this.looksLikeWrite(control)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      // The snackbar is real UI that needs to render/animate/auto-dismiss, so re-enter the zone
      // just for this one call — the only point in the whole class that needs to.
      this.ngZone.run(() => {
        this.snack.open(
          'You have read-only access to this module, so this action is disabled.',
          'Close',
          { duration: 4000 }
        );
      });
    }, true);
  }

  // ─── Dimming: best-effort visual feedback ────────────────────────────────────────────────────

  private scanBody(): void {
    document.querySelectorAll<HTMLElement>(ReadOnlyUiGuard.CONTROL_SELECTOR).forEach(el => this.consider(el));
  }

  private watchOverlayContainer(): void {
    if (this.overlayObserver) return;

    const existing = document.querySelector<HTMLElement>('.cdk-overlay-container');
    if (existing) {
      this.attachOverlayObserver(existing);
      return;
    }

    // The overlay container is normally present by the time the app is interactive, but if not yet,
    // watch only direct children of body (not subtree) until it appears — cheap, since body's direct
    // children change rarely (app root, this container, maybe a toast host), never per table row.
    this.bootstrapObserver = new MutationObserver(() => {
      const container = document.querySelector<HTMLElement>('.cdk-overlay-container');
      if (container) {
        this.bootstrapObserver?.disconnect();
        this.bootstrapObserver = undefined;
        this.attachOverlayObserver(container);
      }
    });
    this.bootstrapObserver.observe(document.body, { childList: true });
  }

  private attachOverlayObserver(container: HTMLElement): void {
    this.overlayObserver = new MutationObserver(() => {
      if (this.overlayTimer) return;
      this.overlayTimer = setTimeout(() => {
        this.overlayTimer = undefined;
        if (this.active) this.scan(container);
      }, ReadOnlyUiGuard.OVERLAY_DEBOUNCE_MS);
    });
    // subtree:true is safe here — the overlay container only ever holds currently-open dialogs,
    // menus and selects, never a full data table.
    this.overlayObserver.observe(container, { childList: true, subtree: true });
    this.scan(container); // whatever is already open when read-only kicks in
  }

  private stopWatchingOverlay(): void {
    this.overlayObserver?.disconnect();
    this.overlayObserver = undefined;
    this.bootstrapObserver?.disconnect();
    this.bootstrapObserver = undefined;
    if (this.overlayTimer) clearTimeout(this.overlayTimer);
    this.overlayTimer = undefined;
  }

  // Watches the rest of the document for content that renders after the fact — a form loading its
  // own data before showing its footer buttons, for instance — which neither the initial scan nor
  // the per-navigation scan can see. The callback does nothing but flag; scanning happens on a
  // trailing timer, so a burst of table-rendering mutations costs a handful of scans total, not one
  // per mutation. This is the same shape as the overlay watcher above, just with a longer debounce
  // because the document is bigger and busier than an overlay ever is.
  private watchBody(): void {
    if (this.bodyObserver) return;
    this.bodyObserver = new MutationObserver(() => {
      this.bodyDirty = true;
      this.scheduleBodyScan();
    });
    this.bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  private scheduleBodyScan(): void {
    if (this.bodyTimer || !this.active) return;
    this.bodyTimer = setTimeout(() => {
      this.bodyTimer = undefined;
      if (!this.active || !this.bodyDirty) return;
      this.bodyDirty = false;
      if (this.active) this.scanBody();
    }, ReadOnlyUiGuard.BODY_DEBOUNCE_MS);
  }

  private stopWatchingBody(): void {
    this.bodyObserver?.disconnect();
    this.bodyObserver = undefined;
    if (this.bodyTimer) clearTimeout(this.bodyTimer);
    this.bodyTimer = undefined;
    this.bodyDirty = false;
  }

  private scan(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>(ReadOnlyUiGuard.CONTROL_SELECTOR).forEach(el => this.consider(el));
  }

  private consider(el: HTMLElement): void {
    if (el.hasAttribute(ReadOnlyUiGuard.MARKER)) return;
    if (this.isAlreadyDisabled(el)) return;
    if (!this.looksLikeWrite(el)) return;
    this.disable(el);
  }

  private isAlreadyDisabled(el: HTMLElement): boolean {
    const asInput = el as HTMLElement & { disabled?: boolean };
    return asInput.disabled === true || el.getAttribute('aria-disabled') === 'true';
  }

  private looksLikeWrite(el: HTMLElement): boolean {
    // A control that only opens something, whose label happens to carry a write word — the
    // Planner's "+ Assign" opens the task panel, and a task named "VAT Return – Post" is just its
    // title. The writes inside whatever it opens are still caught.
    if (el.hasAttribute(ReadOnlyUiGuard.READ_SAFE)) return false;
    const label = [
      el.textContent ?? '',
      el.getAttribute('aria-label') ?? '',
      el.getAttribute('title') ?? '',
      el.getAttribute('matTooltip') ?? '',
    ].join(' ');
    return ReadOnlyUiGuard.WRITE_PATTERN.test(label);
  }

  private disable(el: HTMLElement): void {
    const asInput = el as HTMLElement & { disabled?: boolean };
    el.setAttribute(ReadOnlyUiGuard.MARKER, 'true');
    if ('disabled' in el) asInput.disabled = true;
    el.setAttribute('disabled', 'true');
    el.setAttribute('aria-disabled', 'true');
    el.classList.add('bc-write-disabled');
    if (!el.getAttribute('title')) {
      el.setAttribute('title', 'You have read-only access to this module.');
    }
  }

  private undo(): void {
    document.querySelectorAll<HTMLElement>(`[${ReadOnlyUiGuard.MARKER}]`).forEach(el => {
      const asInput = el as HTMLElement & { disabled?: boolean };
      if ('disabled' in el) asInput.disabled = false;
      el.removeAttribute('disabled');
      el.removeAttribute('aria-disabled');
      el.classList.remove('bc-write-disabled');
      el.removeAttribute(ReadOnlyUiGuard.MARKER);
    });
  }
}
